import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AddPrivatePractice,
  MyWeekItem,
  PortableBookEntry,
  PractitionerBookingHandle,
  PractitionerTimeOff,
  PrivatePracticeSignup,
  Residency,
  SessionUser,
  UpsertBookingHandle,
} from '@salonomy/types';
import { ApiClientError, api, expectOk, unwrap } from '../lib/api.js';
import { qk } from '../lib/query.js';

/**
 * WS11 — Independent stylists & multi-salon chair renters (brief 11; ADR-0012 client-book portability,
 * ADR-0013 practitioner billing matrix). TanStack Query over the generated client, mirroring
 * data/billing.ts / data/scheduling.ts. This is the CONSOLE data layer for the practitioner (identity-
 * spanning) surface: the caller's residencies, the unified "My week" aggregate, the portable sealed
 * book, practitioner-level time off, the salon-side residency lifecycle, and private-practice signup.
 *
 * ARCHITECTURE the server already enforces — the hooks never re-derive it (mirror billing's HONESTY):
 *   • Every aggregate (/me/week, /me/residencies, /me/book) is a READ-SIDE UNION over the caller's OWN
 *     memberships — membershipsFor(userId) resolves identity, then each leg is a NORMAL withTenant() read
 *     under THAT membership's ctx, unioned in app memory and TAGGED by salon. `salonId`/`salonName` on an
 *     item is that per-leg tag, NEVER a cross-tenant join (ADR-0012 §2). The console DISPLAYS the tagged
 *     rows as-is — it never recomputes availability, money, or the union on the client.
 *   • The portable book includes ONLY renter memberships (renterId set); shared books can never match by
 *     construction (ADR-0012 §3). Writes always target exactly ONE tenant.
 *   • "Sick is sick everywhere": practitioner-level time off is GLOBAL to the human (keyed on the
 *     session's user_id, never a request param). The double-booking guard reads it across all tenants.
 *
 * STALETIME POSTURE (mirror data/billing.ts): the identity-spanning aggregates (week / residencies /
 * book / time-off) read live — staleTime 0 — and NONE of these keys are in PERSISTED_PREFIXES
 * (lib/query.ts), so an identity-spanning aggregate is NEVER served from a single tenant's offline
 * IndexedDB replica. priceQuoted on a MyWeekItem is the server's figure, shown as-is.
 */

/**
 * The body for inviting an existing-account practitioner into a chair_renter residency. The generated
 * `InviteResidencyInput` component is NOT a named export from @salonomy/types (it is the swagger
 * inline schema for the body), so the input shape is declared here and cast at the boundary with
 * `as never` — the same idiom data/staff.ts uses for CreateStaff (the server re-validates against the
 * same Zod `InviteResidency` schema). Extends WS6's staff invite: an EXISTING login is never re-credentialed.
 */
export interface InviteResidencyBody {
  /** The practitioner's existing-account email (the invite attaches to THAT human, no new credentials). */
  email: string;
  displayName?: string;
  locationId?: string | null;
  title?: string | null;
  /** Sealed/shared book stance for this residency; `inherit` follows the salon's clientBookMode. */
  bookOverride?: 'inherit' | 'shared' | 'sealed';
}

// ── Practitioner reads (identity-spanning aggregates; LIVE — staleTime 0, never persisted) ──────────

/**
 * The caller's residencies + private practice across ALL salons (the identity-layer union): each
 * membership tagged by salon, with its role, whether it is a private-practice leg, its active/endedAt
 * lifecycle, the renterId (non-null where the caller holds a sealed book), and the weekly schedule.
 * This is the caller's OWN memberships (resolved from the session), never another human's — the
 * "My week" scaffold and the residency surfaces read it to enumerate the legs.
 */
export function useResidencies(enabled = true) {
  return useQuery({
    queryKey: qk.meResidencies,
    enabled,
    staleTime: 0,
    queryFn: async (): Promise<Residency[]> => unwrap(await api.GET('/v1/me/residencies')),
  });
}

/**
 * WS10(b) — the caller's GLOBAL personal booking handle (book.salonomy.com/p/<handle>). An identity-plane
 * read keyed on the HUMAN (the server resolves the owning user from request.requireAuth().userId — there is
 * NO userId param, by design; a userId field would be an authorization hole). UNCLAIMED is the expected
 * empty state, surfaced by the server as a 404 — we map THAT one status to `data: null` ('not claimed yet'),
 * NOT to a query error, so the panel renders the claim form rather than an error banner. Any OTHER failure
 * (401/500/…) is re-thrown and shows as an honest error. Read live (an identity-plane fact, not persisted).
 */
export function useBookingHandle(enabled = true) {
  return useQuery({
    queryKey: qk.meBookingHandle,
    enabled,
    staleTime: 0,
    queryFn: async (): Promise<PractitionerBookingHandle | null> => {
      try {
        return unwrap(await api.GET('/v1/me/booking-handle'));
      } catch (err) {
        // 404 == UNCLAIMED — the deliberate empty state for "you haven't made a booking page yet".
        // Treat it as data:null (not an error) so the panel shows the claim form. Re-throw everything else.
        if (err instanceof ApiClientError && err.status === 404) return null;
        throw err;
      }
    },
  });
}

/**
 * Unified diary — "My week": the caller's appointments across every residency + private practice in the
 * [from, to) window, each row LOCATION-LABELLED (salonName) and carrying the tenant it belongs to
 * (write-back routes there, never elsewhere). `from` inclusive, `to` exclusive (ISO date-times). This is
 * a read-side union — the console renders the tagged rows; it never recomputes the aggregate. Gated on a
 * window being supplied; read live. NB the flagship diary UI is design-gated this slice — this hook
 * proves the data layer behind the scaffold.
 */
export function useMyWeek(range: { from: string; to: string } | null, enabled = true) {
  return useQuery({
    queryKey: range ? qk.meWeek(range.from, range.to) : ['me', 'week', 'none'],
    enabled: Boolean(range) && enabled,
    staleTime: 0,
    queryFn: async (): Promise<MyWeekItem[]> =>
      unwrap(
        await api.GET('/v1/me/week', {
          params: { query: { from: range!.from, to: range!.to } },
        }),
      ),
    placeholderData: keepPreviousData,
  });
}

/**
 * The portable sealed client book: the caller's OWN sealed clients unioned across their renter
 * residencies only (memberships where renterId is set). Each entry is tagged with the salon it
 * physically lives in (rows never travel — the person's ACCESS spans tenants via the union; ADR-0012 §4).
 * Optional text search filters the caller's own sealed rows server-side. lifetimeValue/visitCount are
 * server figures, shown as-is. Read live.
 */
export function usePortableBook(search?: string, enabled = true) {
  const term = search?.trim() ? search.trim() : undefined;
  return useQuery({
    queryKey: qk.meBook(term ?? null),
    enabled,
    staleTime: 0,
    queryFn: async (): Promise<PortableBookEntry[]> =>
      unwrap(
        await api.GET('/v1/me/book', {
          params: { query: term ? { search: term } : {} },
        }),
      ),
    placeholderData: keepPreviousData,
  });
}

/**
 * The caller's practitioner-level time off — GLOBAL to the human, spanning every salon they work in plus
 * private practice ("sick is sick everywhere"). Owned by the session user (never a request param);
 * optional [from, to] date filter. The double-booking guard reads this across all tenants, so the UI
 * shows it as the cross-residency truth it is. Read live.
 */
export function useTimeOff(range: { from?: string; to?: string } = {}, enabled = true) {
  return useQuery({
    queryKey: qk.meTimeOff(range.from ?? null, range.to ?? null),
    enabled,
    staleTime: 0,
    queryFn: async (): Promise<PractitionerTimeOff[]> =>
      unwrap(
        await api.GET('/v1/me/time-off', {
          params: { query: { from: range.from, to: range.to } },
        }),
      ),
  });
}

/**
 * The salon-side residency roster (manager/owner): the chair-rental residencies in the CURRENT salon
 * tenant. `includeEnded` adds the deactivated ones (their salon history + sealed book are preserved).
 * This is a tenant-scoped read (the salon's own staff), distinct from useResidencies (the caller's own
 * cross-salon memberships). Owner/manager-gated on the API; the UI mirrors that with lib/rbac.ts.
 */
export function useSalonResidencies(includeEnded = false, enabled = true) {
  return useQuery({
    queryKey: qk.salonResidencies(includeEnded),
    enabled,
    queryFn: async (): Promise<Residency[]> =>
      unwrap(
        await api.GET('/v1/residencies', {
          params: { query: { includeEnded } },
        }),
      ),
  });
}

// ── Practitioner time-off mutations ────────────────────────────────────────────────────────────────

/**
 * Mark yourself off across ALL your salons + private practice. The owning user is ALWAYS the session —
 * there is no userId field (it would be an authorization hole; ADR-0012). Invalidates the time-off list
 * and the week aggregate (the off period changes what the diary should show). Returns the created entry.
 */
export function useCreateTimeOff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreatePractitionerTimeOffBody): Promise<PractitionerTimeOff> =>
      unwrap(await api.POST('/v1/me/time-off', { body: body as never })),
    onSuccess: () => invalidatePractitioner(qc),
  });
}

/** Remove a practitioner-level time-off entry (only your own — the API gates on the session user). */
export function useDeleteTimeOff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> =>
      expectOk(await api.DELETE('/v1/me/time-off/{id}', { params: { path: { id } } })),
    onSuccess: () => invalidatePractitioner(qc),
  });
}

/**
 * Create-time-off body. The generated `CreatePractitionerTimeOffInput` is the swagger inline body schema
 * (not a named @salonomy/types export); the named export `CreatePractitionerTimeOff` carries a default on
 * `reason`, so it over-requires at the call site — the input shape is declared here and cast with
 * `as never` (the staff.ts idiom). The server re-validates against the same Zod schema.
 */
export interface CreatePractitionerTimeOffBody {
  startDate: string;
  endDate: string;
  reason?: 'holiday' | 'sick' | 'training' | 'other';
  note?: string | null;
}

// ── Residency lifecycle mutations (salon-side; manager/owner) ────────────────────────────────────────

/**
 * Invite an existing-account practitioner to take a chair_renter residency in the current salon (extends
 * WS6's staff invite — never resets their credentials). Returns the created Residency. Invalidates the
 * salon residency roster + the staff list (the new membership shows in both). Owner/manager-gated.
 */
export function useInviteResidency() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: InviteResidencyBody): Promise<Residency> =>
      unwrap(await api.POST('/v1/residencies/invite', { body: body as never })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['residencies'] });
      void qc.invalidateQueries({ queryKey: qk.staff });
    },
  });
}

/**
 * End a chair-rental residency: deactivate the membership (staff.active → false, residency_ended_at
 * stamped) and detach future availability, leaving the salon's HISTORY and the sealed book intact
 * (ADR-0012 §4). Keyed on the residency's staffId in this tenant. Returns the now-ended Residency.
 * Invalidates the salon roster + the staff list. Owner/manager-gated.
 */
export function useEndResidency() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (staffId: string): Promise<Residency> =>
      unwrap(await api.POST('/v1/residencies/{staffId}/end', { params: { path: { staffId } } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['residencies'] });
      void qc.invalidateQueries({ queryKey: qk.staff });
    },
  });
}

// ── Private-practice signup mutations ────────────────────────────────────────────────────────────────

/**
 * Brand-new practitioner signup (PRE-SESSION): create the account AND their own one-chair studio tenant
 * in one step (brief 11 §1; ADR-0013 §4 — a salon whose owner is its only chair, NO special SKU).
 * Returns the SessionUser for the new login (the server sets the session cookie). The caller (a signup
 * surface) wires the returned principal into the auth context — this hook does not touch session state.
 * Not invalidated here: there is nothing in the cache yet (no session).
 */
export function usePrivatePracticeSignup() {
  return useMutation({
    mutationFn: async (body: PrivatePracticeSignup): Promise<SessionUser> =>
      unwrap(await api.POST('/v1/auth/private-practice/signup', { body: body as never })),
  });
}

/**
 * AUTHENTICATED practitioner adds their OWN studio to the account they already use (their second-or-later
 * membership) — one login across their whole working life (brief 11 non-negotiable #7). Returns the
 * SessionUser now scoped to the new tenant (the server switches context). The caller wires the returned
 * principal into the auth context and clears the cache for the new tenant; this hook invalidates the
 * caller's residency list (a new membership now exists).
 */
export function useAddPrivatePractice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: AddPrivatePractice): Promise<SessionUser> =>
      unwrap(await api.POST('/v1/private-practice', { body: body as never })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.meResidencies });
      void qc.invalidateQueries({ queryKey: qk.memberships });
    },
  });
}

// ── Personal booking-handle mutation (claim / update / enable-disable) ───────────────────────────────

/**
 * WS10(b) — CLAIM or UPDATE the caller's personal booking handle (PUT /v1/me/booking-handle). This ONE
 * route covers everything: first claim, renaming the display name, AND enable/disable — disabling is just
 * this same PUT with `enabled: false` (there are NO separate /enable or /disable routes). The owning user
 * is ALWAYS the authenticated session — there is NO userId in the body, by design (the server uses
 * request.requireAuth().userId; a userId field would be an authorization hole; ADR-0012).
 *
 * 409 IS THE TWO-FACED ONE: the server returns it BOTH for an ineligible caller (no online-bookable leg)
 * AND for a case-insensitive duplicate of someone else's handle — SAME status, DIFFERENT envelope message.
 * This hook does NOT try to disambiguate; the caller reads err.message off the ApiClientError envelope and
 * decides where to surface it (taken → the handle field; ineligible → the gated explanation). Invalidates
 * the handle read so the panel reflects the new claim/state.
 */
export function useUpsertBookingHandle() {
  const qc = useQueryClient();
  return useMutation({
    // body cast `as never` (the staff.ts boundary idiom): the generated `UpsertBookingHandleInput` is the
    // swagger inline body schema, not a named @salonomy/types export — the server re-validates against the
    // same Zod `UpsertBookingHandle`. NEVER add a userId here (authorization invariant; see the doc above).
    mutationFn: async (body: UpsertBookingHandle): Promise<PractitionerBookingHandle> =>
      unwrap(await api.PUT('/v1/me/booking-handle', { body: body as never })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.meBookingHandle });
    },
  });
}

// ── Invalidation helper ──────────────────────────────────────────────────────────────────────────────

/**
 * A time-off change spans every residency, so it can shift what "My week" should show — re-read the
 * whole practitioner aggregate surface (time-off + the week union). The `['me']` keys for week/time-off
 * are covered; residencies are not touched by a time-off write so they are left alone.
 */
function invalidatePractitioner(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: ['me', 'time-off'] });
  void qc.invalidateQueries({ queryKey: ['me', 'week'] });
}
